import { useContext } from 'react';

import { CLIENT_POINT_API_BASE } from '../../const';
import Translation from '../../types/Translation';
import { BookingStatusES, BookingType } from '../../types/booking';
import i18n from '../Locale';

import { EventSourceContext } from './index';

export enum EventSourceType {
	BOOKING = 'booking',
	ORDER = 'order',
}

export enum EventSourceAction {
	UPDATE_ORDER_STATUS = 'UPDATE_ORDER_STATUS',
	UPDATE_BOOKING_STATUS = 'UPDATE_BOOKING_STATUS',
	CLOSE_CONNECTION = 'CLOSE_CONNECTION',
	PING = 'PING',
}

type EventSourceCommonData = {
	action: EventSourceAction;
	order?: Partial<{
		preparingTime: number;
		approvedTimestamp: string;
		externalOrderNum: number;
	}>;
	booking?: Partial<BookingType>;
	guid?: string;
	step?: BookingStatusES;
	updated: string;
};

type EventSourceResponse = {
	data: EventSourceCommonData;
	error: null | keyof Translation;
};

export type EventSourceErrorCallback = (message: string) => void;
export type EventSourceMessageCallback = (data: EventSourceCommonData) => void;

export type CustomEventType = {
	token: string;
	eventSource: EventSource;
	messageCallbacks: EventSourceMessageCallback[];
	errorCallbacks: EventSourceErrorCallback[];
};

export const useEventSource = () => {
	const { setEventSourceList } = useContext(EventSourceContext);

	const onMessage = (token: string, callback: EventSourceMessageCallback) => {
		setEventSourceList((prevState) => {
			const currentCustomEventSource = prevState.find((i) => i.token === token);

			if (!currentCustomEventSource) return [...prevState];

			const newCustomEventSource = { ...currentCustomEventSource };
			if (callback) newCustomEventSource.messageCallbacks.push(callback);

			return prevState.map((i) => (i.token === token ? newCustomEventSource : i));
		});
	};

	const onError = (token: string, callback: EventSourceErrorCallback) => {
		setEventSourceList((prevState) => {
			const currentCustomEventSource = prevState.find((i) => i.token === token);

			if (!currentCustomEventSource) return [...prevState];

			const newCustomEventSource = { ...currentCustomEventSource };
			newCustomEventSource.errorCallbacks.push(callback);

			return prevState.map((i) => (i.token === token ? newCustomEventSource : i));
		});
	};

	const callErrorCallbacks = (token: string, error: keyof Translation) => {
		if (error === 'booking.notFound') return; // fix for bug on server
		if (error === 'order.notFound') return; // fix for bug on server

		let cbs: EventSourceErrorCallback[] = [];
		const errorMessage = i18n(error) || i18n('unexpected_error');

		setEventSourceList((prevState) => {
			const currentCustomEventSource = prevState.find((i) => i.token === token);

			if (currentCustomEventSource) {
				cbs = currentCustomEventSource.errorCallbacks;
			}

			return prevState;
		});

		setTimeout(() => {
			cbs.forEach((i) => i(errorMessage));

			closeConnection(token);
		}, 0);
	};

	const callMessageCallbacks = (token: string, data: EventSourceCommonData) => {
		let cbs: EventSourceMessageCallback[] = [];

		setEventSourceList((prevState) => {
			const currentCustomEventSource = prevState.find((i) => i.token === token);

			if (currentCustomEventSource) {
				cbs = currentCustomEventSource.messageCallbacks;
			}

			return prevState;
		});

		if (data.action !== EventSourceAction.PING) {
			setTimeout(() => {
				cbs.forEach((i) => i?.(data));
			}, 0);
		}
	};

	const closeConnection = (token: string) => {
		setEventSourceList((prevState) => {
			const foundIndex = prevState.findIndex((i) => i.token === token);
			const newState = [...prevState];

			if (foundIndex === -1) return [...newState];

			const customEventSourceForClose = newState.splice(foundIndex, 1);

			customEventSourceForClose[0].eventSource.close();

			return newState;
		});
	};

	const subscribe = (type: EventSourceType, token: string) => {
		const url = `https://${CLIENT_POINT_API_BASE}/${type}/status?jwt=${token}`;
		const eventSource = new EventSource(url);

		eventSource.addEventListener('message', (event: MessageEvent) => {
			try {
				const { data, error }: EventSourceResponse = JSON.parse(event.data);

				if (error) callErrorCallbacks(token, error);
				if (data)
					setTimeout(() => {
						callMessageCallbacks(token, data);
					}, 333);
			} catch (e: any) {
				callErrorCallbacks(token, e.message);
			}
		});

		eventSource.addEventListener('error', () => {
			callErrorCallbacks(token, 'unexpected_error');
		});

		setEventSourceList((prevState) => {
			return [
				...prevState,
				{
					token,
					eventSource,
					messageCallbacks: [],
					errorCallbacks: [],
				},
			];
		});
	};

	return {
		subscribe,
		onMessage,
		onError,
		closeConnection,
	};
};
